'use client'

import { LuX } from 'react-icons/lu'

import { Button } from '@/core/components/form/button/button'
import SafeImage from '@/features/veluvit/components/ui/media-image'
import { toast } from '@/core/lib/toast'

import { TextLink } from '@/features/veluvit/components/overlay/layer-parts'
import { useCatalogMedia } from '@/features/veluvit/hooks/use-app-data'
import { josa } from '@/features/veluvit/lib/format'
import { isStoryChar, workFor } from '@/features/veluvit/lib/rules'
import { closeTop } from '@/features/veluvit/stores/overlay.store'
import { roomFor } from '@/features/veluvit/stores/session.store'

import type { Catalog } from '@/features/veluvit/types/catalog.types'

export type AppSheetMoment = 'default' | 'exit' | 'branch'

/** 화자는 회사가 아니라 캐릭터다. 기능이 아니라 관계를 말한다 (레퍼런스 appSheetCopy — 앱설치_유도문구.md) */
function copyFor(
  catalog: Catalog,
  id: string | undefined,
  when: AppSheetMoment
) {
  const c = id ? catalog.chars[id] : undefined
  const nm = c ? c.short || c.name : ''
  if (c && isStoryChar(c)) {
    if (when === 'branch')
      return {
        h: '여기서 선택이 갈려요',
        s: '앱에서 이어서 고를 수 있어요',
        cta: '앱 설치하고 이어서 고르기',
      }
    // 회차 대신 장면 — 어느 장면이든 순서 없이 들어갈 수 있다
    const started = !!(id && roomFor(id))
    const scenes = id ? workFor(catalog, id)?.branches?.length || 0 : 0
    return {
      h: started
        ? '이야기가 여기서 멈춰 있어요'
        : '아직 이야기를 시작하지 않았어요',
      s: scenes
        ? `${scenes}개 장면 중 어디서든 앱에서 이어서 들어갈 수 있어요`
        : '앱에서 바로 이어서 할 수 있어요',
      cta: '앱 설치하고 이어서 하기',
    }
  }
  if (!nm || !id)
    return {
      h: '캐릭터가 먼저 말을 걸어요',
      s: '25명이 각자 다른 말투로 기다리고 있어요',
      cta: '앱 설치하고 먼저 연락받기',
    }
  // 이름은 제목에서 이미 불렀다 — 버튼은 짧게 행동만
  const cta = '앱 설치하고 먼저 연락받기'
  switch (when) {
    case 'exit':
      return {
        h: `지금 나가면 ${josa(nm, '는/은')} 계속 기다려요`,
        s: '알림을 켜 두면 먼저 말을 걸어올 거예요',
        cta,
      }
    default:
      return {
        h: `${josa(nm, '가/이')} 먼저 연락할 거예요`,
        s: '앱에서는 답장을 기다리지 않아도 돼요.\n읽기 전에 먼저 말을 걸어오니까요.',
        cta,
      }
  }
}

/** 앱 설치 유도 시트 */
export default function AppSheet({
  workId,
  when = 'default',
}: {
  workId?: string
  when?: AppSheetMoment
}) {
  const { catalog, thumb } = useCatalogMedia()
  const t = copyFor(catalog, workId, when)
  return (
    <>
      <div className="flex justify-end">
        <Button
          type="button"
          aria-label="닫기"
          onClick={() => closeTop()}
          className="grid size-9 cursor-pointer place-items-center rounded-full text-t3 transition-colors hover:bg-foreground/[0.07] hover:text-t1 focus-visible:bg-foreground/[0.07]"
          variant="bare"
        >
          <LuX aria-hidden="true" className="size-5" />
        </Button>
      </div>
      <div className="text-center">
        {workId && catalog.chars[workId] ? (
          <div className="relative mx-auto size-18 overflow-hidden rounded-full">
            <SafeImage
              src={thumb(workId)}
              alt=""
              fill
              sizes="72px"
              className="object-cover"
              appearance="plain"
            />
          </div>
        ) : (
          <div className="mx-auto grid size-16 place-items-center rounded-panel bg-[linear-gradient(135deg,#ff6a55,#f2533d)] text-inverse text-c13 font-black tracking-[-0.04em]">
            veluvit
          </div>
        )}
        <h3 className="mt-3.5 text-c20 font-bold break-keep text-balance">
          {t.h}
        </h3>
        <p className="mt-1.5 text-c12 leading-[1.6] break-keep whitespace-pre-line text-t3">
          {t.s}
        </p>
        <Button
          variant="accent"
          size="lg"
          shape="pill"
          bold
          className="w-full mt-4.5"
          onClick={() => {
            closeTop()
            toast.show('앱 스토어로 이동해요 (프로토타입)')
          }}
        >
          {t.cta}
        </Button>
        <TextLink className="mt-3" onClick={() => closeTop()}>
          다음에 받을게요
        </TextLink>
      </div>
    </>
  )
}
